import { ActorTree } from '../types/actor-types';

interface ActorTreeNode extends ActorTree {
  children: ActorTreeNode[];
  level: number;
}

export function filterTreeBySearch(
  treeData: ActorTreeNode[],
  searchQuery: string,
  actors: ActorTree[]
): ActorTreeNode[] {
  if (!searchQuery.trim()) {
    return treeData;
  }

  const query = searchQuery.toLowerCase().trim();
  const matchingIds = new Set<number>();
  const ancestorIds = new Set<number>();

  // Find all matching nodes
  const findMatches = (nodes: ActorTreeNode[]) => {
    nodes.forEach(node => {
      const nameMatch = node.name?.toLowerCase().includes(query);
      const idMatch = node.actorId.toString().includes(query);
      
      if (nameMatch || idMatch) {
        matchingIds.add(node.actorId);
        
        // Add all ancestors
        let currentActorId = node.parentActorId;
        while (currentActorId) {
          ancestorIds.add(currentActorId);
          const parent = actors.find(a => a.actorId === currentActorId);
          currentActorId = parent?.parentActorId;
        }
      }
      
      if (node.children.length > 0) {
        findMatches(node.children);
      }
    });
  };

  findMatches(treeData);

  // Filter tree to only show matching nodes and ancestors
  const filterNodes = (nodes: ActorTreeNode[]): ActorTreeNode[] => {
    return nodes
      .map(node => {
        const isMatch = matchingIds.has(node.actorId);
        const isAncestor = ancestorIds.has(node.actorId);
        const filteredChildren = filterNodes(node.children);

        if (isMatch || isAncestor || filteredChildren.length > 0) {
          return { ...node, children: filteredChildren };
        }
        return null;
      })
      .filter((node): node is ActorTreeNode => node !== null);
  };

  return filterNodes(treeData);
}

export function countMatches(
  treeData: ActorTreeNode[],
  searchQuery: string
): number {
  if (!searchQuery.trim()) return 0;
  
  let count = 0;
  const query = searchQuery.toLowerCase().trim();
  
  const countInNodes = (nodes: ActorTreeNode[]) => {
    nodes.forEach(node => {
      const nameMatch = node.name?.toLowerCase().includes(query);
      const idMatch = node.actorId.toString().includes(query);
      if (nameMatch || idMatch) count++;
      countInNodes(node.children);
    });
  };
  
  countInNodes(treeData);
  return count;
}

export function isNodeMatch(
  node: ActorTreeNode,
  searchQuery: string
): boolean {
  if (!searchQuery.trim()) return false;
  const query = searchQuery.toLowerCase().trim();
  return (
    node.name?.toLowerCase().includes(query) ||
    node.actorId.toString().includes(query)
  );
}

export function filterTreeByEmptyLocations(
  treeData: ActorTreeNode[],
  hideEmpty: boolean
): ActorTreeNode[] {
  if (!hideEmpty) {
    return treeData;
  }

  // Filter tree to only show nodes with machines or that have descendants with machines
  const filterNodes = (nodes: ActorTreeNode[]): ActorTreeNode[] => {
    return nodes
      .map(node => {
        // Recursively filter children first
        const filteredChildren = filterNodes(node.children);

        // Keep node if:
        // 1. It has machines (machinesCount > 0), OR
        // 2. It has children that passed the filter (have machines in their subtree)
        const hasMachines = node.machinesCount && node.machinesCount > 0;
        const hasValidChildren = filteredChildren.length > 0;

        if (hasMachines || hasValidChildren) {
          return { ...node, children: filteredChildren };
        }
        return null;
      })
      .filter((node): node is ActorTreeNode => node !== null);
  };

  return filterNodes(treeData);
}
